import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentReady(this.#preloadImages);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else if (!(event instanceof FocusEvent)) {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (item.closest('[slot="overflow"]') !== null).toString();

    this.#deactivate();
    this.#state.activeItem = item;
    this.#setExpanded(item, true);

    const submenu = findSubmenu(item);

    if (!submenu) return;

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  };

  toggle = (event) => {
    this.#debouncedActivateHandler.cancel();

    if (event.detail === 0 && event.target.ariaExpanded === 'true') {
      this.#debouncedDeactivate.cancel();
      this.dataset.overflowExpanded = 'false';
      this.#deactivate();
      return;
    }

    this.#activateHandler(event);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (event instanceof FocusEvent && findMenuItem(event.target)?.parentElement?.contains(event.relatedTarget)) return;
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce((event) => {
    this.dataset.overflowExpanded = 'false';

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item != this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.style.setProperty('--submenu-height', '0px');

    if (!item) return;

    this.#state.activeItem = null;
    this.#setExpanded(item, false);
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Reflect the open state on the item's disclosure button and, for overflow items, on the "More" button.
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpanded(item, expanded) {
    const inOverflow = item.closest('[slot="overflow"]') !== null;
    if (item.hasAttribute('aria-controls') || inOverflow) item.ariaExpanded = expanded.toString();

    const more = this.#moreButton;
    if (more && inOverflow) more.ariaExpanded = expanded.toString();
  }

  /**
   * @returns {HTMLElement | null}
   */
  get #moreButton() {
    return this.querySelector('[slot="more"] > .menu-list__link');
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const activeElement = document.activeElement;
    const menuItems = Array.from(this.querySelectorAll('.menu-list__list-item > .menu-list__link'));
    const currentIndex = menuItems.indexOf(activeElement);

    if (currentIndex < 0 && ['ArrowLeft', 'ArrowRight', 'ArrowDown'].includes(event.key)) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        if (currentIndex > 0) {
          menuItems[currentIndex - 1].focus();
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (currentIndex < menuItems.length - 1) {
          menuItems[currentIndex + 1].focus();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.#debouncedActivateHandler.cancel();
        this.#activateHandler(event);
        const submenu = findSubmenu(findMenuItem(activeElement));
        if (submenu) {
          const firstLink = submenu.querySelector('a, button');
          if (firstLink) firstLink.focus();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.#debouncedActivateHandler.cancel();
        this.#debouncedDeactivate.cancel();
        const active = this.#state.activeItem ?? findMenuItem(activeElement);
        const opener = active?.closest('[slot="overflow"]') ? this.#moreButton : active;
        opener?.focus({ preventScroll: true });
        this.#deactivate();
        break;
    }
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const more = element.closest('[slot="more"]');
  if (more) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(more.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element.closest('.menu-list__list-item')?.querySelector(':scope > .menu-list__link') ?? null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
